import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { COLORS, FONT_FAMILY } from './styles';
import {
  isStrictApiMode,
  siteAuthLoginUrl,
  siteAuthLogoutUrl,
  siteAuthStatusUrl
} from './siteAccessApi';

const SiteAccessContext = createContext({
  isSiteGated: false,
  signOutOfSite: async () => {}
});

export const useSiteAccess = () => useContext(SiteAccessContext);

function SitePasswordScreen({ onSuccess, errorText }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSubmitting(true);
    try {
      const res = await fetch(siteAuthLoginUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password })
      });
      if (!res.ok) {
        setLocalError('That password is not valid.');
        setPassword('');
        return;
      }
      onSuccess();
    } catch (err) {
      setLocalError('Could not reach the server. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: `linear-gradient(180deg, ${COLORS.panel} 0%, ${COLORS.offWhite} 100%)`,
        fontFamily: FONT_FAMILY
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          padding: 32,
          borderRadius: 20,
          background: '#fff',
          boxShadow: '0 12px 40px rgba(126, 82, 199, 0.12), 0 2px 8px rgba(0,0,0,0.04)',
          border: '1px solid rgba(163, 137, 244, 0.25)'
        }}
      >
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: COLORS.purple,
            textAlign: 'center',
            margin: '0 0 8px 0',
            lineHeight: 1.2
          }}
        >
          Pet Jr
        </h1>
        <p
          style={{
            textAlign: 'center',
            color: '#555',
            fontSize: 15,
            margin: '0 0 24px 0',
            lineHeight: 1.4
          }}
        >
          This site is private. Enter the site password to continue.
        </p>
        <form onSubmit={handleSubmit}>
          {(errorText || localError) && (
            <div
              style={{
                marginBottom: 16,
                padding: 10,
                borderRadius: 10,
                background: 'rgba(239, 68, 68, 0.1)',
                color: '#b91c1c',
                fontSize: 14,
                textAlign: 'center'
              }}
            >
              {errorText || localError}
            </div>
          )}
          <label
            style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: '#333',
              marginBottom: 6
            }}
            htmlFor="petjr-site-pw"
          >
            Site password
          </label>
          <input
            id="petjr-site-pw"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '12px 14px',
              fontSize: 16,
              borderRadius: 12,
              border: '2px solid #c4b5fd',
              marginBottom: 18
            }}
          />
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '14px 16px',
              fontSize: 16,
              fontWeight: 700,
              color: '#fff',
              background: COLORS.purple,
              border: 'none',
              borderRadius: 12,
              cursor: submitting ? 'wait' : 'pointer',
              boxShadow: '0 4px 14px rgba(126, 82, 199, 0.35)'
            }}
          >
            {submitting ? 'Checking…' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: COLORS.offWhite,
        fontFamily: FONT_FAMILY,
        color: COLORS.purple
      }}
    >
      <span style={{ fontSize: 16, fontWeight: 600 }}>Loading…</span>
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: COLORS.offWhite,
        fontFamily: FONT_FAMILY
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <p style={{ color: '#b91c1c', fontWeight: 600, marginBottom: 16 }}>{message}</p>
        <button
          type="button"
          onClick={onRetry}
          style={{
            padding: '12px 20px',
            fontWeight: 600,
            borderRadius: 12,
            border: 'none',
            background: COLORS.purple,
            color: '#fff',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export function SiteAccessProvider({ children }) {
  const [phase, setPhase] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const [serverGated, setServerGated] = useState(false);

  const runStatusCheck = useCallback(async () => {
    setLoadError('');
    setPhase('loading');
    try {
      const res = await fetch(siteAuthStatusUrl(), { credentials: 'include' });
      if (!res.ok) {
        setServerGated(false);
        if (isStrictApiMode()) {
          setLoadError('The server could not confirm access. Check the API and try again.');
          setPhase('error');
        } else {
          setPhase('app');
        }
        return;
      }
      const data = await res.json();
      setServerGated(!!data.siteAccessEnabled);
      if (!data.siteAccessEnabled) {
        setPhase('app');
        return;
      }
      if (data.authenticated) {
        setPhase('app');
      } else {
        setPhase('login');
      }
    } catch (e) {
      setServerGated(false);
      if (isStrictApiMode()) {
        setLoadError('Could not reach the app server. If this is a private deploy, confirm REACT_APP_API_URL and CORS.');
        setPhase('error');
      } else {
        setPhase('app');
      }
    }
  }, []);

  useEffect(() => {
    runStatusCheck();
  }, [runStatusCheck]);

  const signOutOfSite = useCallback(async () => {
    try {
      await fetch(siteAuthLogoutUrl(), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (e) {
      // still show login
    }
    setPhase('login');
  }, []);

  const onLoginSuccess = useCallback(() => {
    setServerGated(true);
    setPhase('app');
  }, []);

  const value = useMemo(
    () => ({
      isSiteGated: serverGated,
      signOutOfSite
    }),
    [serverGated, signOutOfSite]
  );

  if (phase === 'loading') {
    return <Loading />;
  }

  if (phase === 'error') {
    return <ErrorPanel message={loadError} onRetry={runStatusCheck} />;
  }

  if (phase === 'login') {
    return <SitePasswordScreen onSuccess={onLoginSuccess} />;
  }

  return <SiteAccessContext.Provider value={value}>{children}</SiteAccessContext.Provider>;
}
